import { Button } from '@/components/Button';
import { useLocale } from '@/providers/LocaleProvider';
import { KLAYO_COLORS } from '@/themes';
import { Box, Stack, Typography } from '@mui/material';
import { ChangeEvent, useRef } from 'react';

/** Mirrors the API's rule for manual-course images (TrainingCourseImage on the server). */
export const TRAINING_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const TRAINING_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export const isValidTrainingImage = (file: File) =>
  TRAINING_IMAGE_TYPES.includes(file.type) && file.size <= TRAINING_IMAGE_MAX_BYTES;

interface TrainingImageFieldProps {
  previewUrl?: string;
  error?: boolean;
  disabled?: boolean;
  onSelect: (file: File) => void;
  onRemove: () => void;
}

/** Image picker for a manual training course, previewed at the course card's 5:3 shape (.klayo_card_image). */
export const TrainingImageField = ({
  previewUrl,
  error,
  disabled,
  onSelect,
  onRemove,
}: TrainingImageFieldProps) => {
  const { translate } = useLocale();
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared so picking the same file again (e.g. after Remove) still fires onChange.
    e.target.value = '';
    if (file) onSelect(file);
  };

  return (
    <Stack spacing={1}>
      <Typography variant='body2'>{translate('dialogs.trainingImageOptional')}</Typography>
      <Stack direction='row' spacing={2} alignItems='center'>
        <Box
          sx={{
            width: 160,
            maxWidth: '40%',
            aspectRatio: '5 / 3',
            borderRadius: '8px',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: KLAYO_COLORS.PrussianBluePB050,
          }}
        >
          {previewUrl && (
            <img
              src={previewUrl}
              alt=''
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}
        </Box>
        <Stack spacing={1} alignItems='flex-start'>
          <Button
            size='sm'
            variant='outlined'
            label={translate(previewUrl ? 'dialogs.changeImage' : 'dialogs.uploadImage')}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          />
          {previewUrl && (
            <Button
              size='sm'
              variant='text'
              label={translate('dialogs.removeImage')}
              disabled={disabled}
              onClick={onRemove}
            />
          )}
        </Stack>
      </Stack>
      <Typography variant='caption' color={error ? 'error' : 'text.secondary'}>
        {translate(error ? 'dialogs.trainingImageInvalid' : 'dialogs.trainingImageHint')}
      </Typography>
      <input
        ref={inputRef}
        type='file'
        hidden
        accept={TRAINING_IMAGE_TYPES.join(',')}
        onChange={onChange}
        data-testid='training-image-input'
      />
    </Stack>
  );
};
